import { useRef, useEffect, useState } from 'react';
import {
  check_circle_primary,
  chevron_right_gray400,
  drag_and_drop_icon,
  picture_icon,
  StarsSecondaryIcon,
  trash_can_primary,
  x_gray400
} from '@assets/icons';
import {
  AIRecommendedChildOptionValue,
  AIRecommendedTag,
  Chip,
  Frame,
  GuideModal,
  SimpleCharacterNumberInput,
  ImageUploaderMenuTop,
  LoadingSpinner,
  SearchInput,
  SecureImage,
  Select,
  Text
} from '@components';
import {
  ChipPaperType,
  FemaleBodyChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  ImageUploaderModalType,
  FrameChildOptionValueEnum,
  SexualBodyPartChildOptionValueEnum,
  SexualPositionChildOptionValueEnum,
  styleConstant,
  VerticalAngleChildOptionValueEnum,
  ToastTypeEnum,
  ToastMessageEnum
} from '@constant';
import { useImageUploader, useToast } from '@hooks';
import { Button, MenuItem, styled } from '@mui/material';
import { colorSet } from '@theme';
import { IOptionList } from '@types';

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const ScrollList = styled('div')({
  height: '100%',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  },

  '&.has-scrollbar': {
    paddingRight: '8px'
  }
});

const ImageUploaderImageUploadPage = () => {
  const {
    handleBreadCrumbClick,
    isCreate,
    handlePageCancelClick,
    needImageAttach,
    isRequestPending,
    handleSaveButtonClick,
    isUploadDisabled,

    selectedSexualPosition,
    handleSelectedSexualPositionChange,
    inputKeyword,
    autocompleteList,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicNameUpdate,

    imageList,
    handleImageUploadButtonClick,
    handleAttachDrop,
    handleAttachDragOver,
    handleAttachDragEnter,
    handleDragStart,
    handleDragOver,
    handleDragDrop,

    handleImageFocus,
    focusedImage,
    handleImageDelete,
    updateFocusedImageChildOptions,
    isFetchAIRecommendationLoading,
    tagInputKeyword,
    tagAutocompleteList,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    handleTagAutocompleteChange,
    addFocusedImageTag,
    deleteFocusedImageTag,

    modalType,
    isModalVisible,
    setIsModalVisible,
    modalTitle,
    modalContent,
    handleModalConfirmClick,
    isSaveAttempted
  } = useImageUploader();

  const [imageLabellingFormHeight, setImageLabellingFormHeight] = useState<number | undefined>(undefined);
  // 스크롤 리스트 스크롤바 여부에 따른 CSS 분기를 위한 상태
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const imageLabellingFormRef = useRef<HTMLDivElement>(null);
  const scrollListRef = useRef<HTMLDivElement>(null);

  const horizontalGap = 24;
  const itemNumberPerRow = 6;
  const sumOfHorizontalGaps = horizontalGap * (itemNumberPerRow - 1);
  const itemSize = `calc((100% - ${sumOfHorizontalGaps}px) / ${itemNumberPerRow})`;

  const { showToast } = useToast();

  const createModalButtonText = () => {
    if (modalType === ImageUploaderModalType.IMAGE_UPLOAD_CANCEL) {
      return '중단하기';
    } else if (modalType === ImageUploaderModalType.IMAGE_DELETE_CONFIRM) {
      return '삭제하기';
    } else {
      return '확인';
    }
  };

  const createModalButtonVariant = () => {
    if (
      modalType === ImageUploaderModalType.IMAGE_UPLOAD_CANCEL ||
      modalType === ImageUploaderModalType.IMAGE_DELETE_CONFIRM ||
      modalType === ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS
    ) {
      return 'contained';
    } else {
      return 'outlined';
    }
  };

  const createModalButtonCount = () => {
    if (
      modalType === ImageUploaderModalType.IMAGE_UPLOAD_CANCEL ||
      modalType === ImageUploaderModalType.IMAGE_DELETE_CONFIRM
    ) {
      return 2;
    } else {
      return 1;
    }
  };

  // 파일 선택 버튼란 + 이미지 보드의 height가 이미지 라벨링 폼과 같도록 하기 위해, 이미지 라벨링 폼의 height를 local state로 관리
  useEffect(() => {
    if (imageLabellingFormRef.current) {
      setImageLabellingFormHeight(imageLabellingFormRef.current.getBoundingClientRect().height);

      // ResizeObserver를 사용하여 크기 변화 감지
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setImageLabellingFormHeight(entry.target.getBoundingClientRect().height);
        }
      });

      resizeObserver.observe(imageLabellingFormRef.current);

      // 클린업 함수
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  // imageList가 변경될 때마다 스크롤바 존재 여부 체크
  useEffect(() => {
    const checkScrollbar = () => {
      if (scrollListRef.current) {
        const hasVerticalScrollbar = scrollListRef.current.scrollHeight > scrollListRef.current.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    return () => window.removeEventListener('resize', checkScrollbar);
  }, [imageList]);

  return (
    <>
      <ImageUploaderMenuTop />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100vh - ${styleConstant.menuTopHeight}px)`
        }}
      >
        {/* 상단부: 헤더 및 필터&작품명 */}
        <Frame width="100%" padding="24px 40px 24px 40px">
          {/* 헤더 */}
          <div
            style={{
              height: 56,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginBottom: '40px'
            }}
          >
            <div>
              <div
                onClick={handleBreadCrumbClick}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: '4px',
                  cursor: 'pointer'
                }}
              >
                <Text variant="body1" color="primary" marginRight="10px">
                  19금 이미지 리스트
                </Text>
                <img src={chevron_right_gray400} style={{ width: 16, height: 16 }} />
              </div>
              <Text variant="h2" fontWeight="bold">
                {isCreate ? '이미지 업로드' : '이미지 정보 수정'}
              </Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
              <Button onClick={handlePageCancelClick} variant="contained" color="secondary">
                취소
              </Button>
              <Button
                disabled={needImageAttach || isRequestPending}
                onClick={handleSaveButtonClick}
                variant="contained"
                color="primary"
              >
                저장
              </Button>
            </div>
          </div>

          {/* 필터&작품명 */}
          <div
            style={{
              height: 76,
              display: 'flex',
              flexDirection: 'row',
              gap: 24
            }}
          >
            <Select label="분류" boxstyle={{ width: 170 }} value={'19금'} disabled>
              <MenuItem value="19금">19금</MenuItem>
            </Select>
            <Select
              label="하위 스페셜 단추"
              boxstyle={{ width: 170 }}
              value={selectedSexualPosition}
              onChange={handleSelectedSexualPositionChange}
            >
              <MenuItem value={'체위'} disabled>
                체위
              </MenuItem>
              {Object.entries(SexualPositionChildOptionValueEnum)
                .filter(([label]) => label !== '모든 체위')
                .map(([label, value]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
            <SearchInput
              label="작품명"
              placeholder="작품명 입력 후 이미지를 업로드할 수 있습니다."
              boxstyle={{ width: 344 }}
              value={inputKeyword}
              options={autocompleteList}
              onInputChange={handleInputKeywordChange}
              onAutocompleteChange={handleAutocompleteChange}
              onComicNameUpdate={handleComicNameUpdate}
              isError={isSaveAttempted && inputKeyword.trim().length === 0}
              showSubIcon={true}
            />
          </div>
        </Frame>
        <div style={{ borderBottom: `1px solid ${colorSet.gray300}` }} />

        {/* 하단부: 파일 선택 버튼 & 이미지 보드 및 이미지 라벨링 폼 */}
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'flex-start', flexGrow: 1 }}>
          {/* 파일 선택 버튼 & 이미지 보드 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                height: styleConstant.fileSelectButtonLayoutImageUploadPage,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: '16px 16px 16px 40px',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12
                }}
              >
                <img src={picture_icon} style={{ width: 44, height: 44 }} />
                {isUploadDisabled ? (
                  <Text variant="body1" color="error" fontWeight="bold">
                    이미지를 업로드하려면 먼저 작품명을 입력하세요.
                  </Text>
                ) : (
                  <div>
                    <Text variant="body1" color={colorSet.gray500} fontWeight="bold" marginBottom="4px">
                      파일을 아래에 끌어오세요.
                    </Text>
                    <Text variant="body1" color={colorSet.gray500}>
                      10MB 이하의 jpg, png 파일만 업로드 가능합니다.
                    </Text>
                  </div>
                )}
              </div>

              <input
                type="file"
                multiple
                accept="image/jpg, image/png"
                onChange={(e) => {
                  if (isFetchAIRecommendationLoading) {
                    showToast(
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Text variant="body1" color={colorSet.white} fontWeight="bold">
                          {ToastMessageEnum.CANNOT_ATTACH_IMAGE_DURING_AI_FETCHING}
                        </Text>
                        <Text variant="body1" color={colorSet.white}>
                          {ToastMessageEnum.RETRY_AFTER_AI_FETCHING}
                        </Text>
                      </div>,
                      ToastTypeEnum.ERROR
                    );
                  } else {
                    handleImageUploadButtonClick(e);
                  }
                }}
                style={{ display: 'none' }}
                id="image-upload-input"
              />
              <Button
                disabled={isUploadDisabled}
                variant="contained"
                color="point"
                sx={{ width: 147, borderRadius: '22px' }}
                onClick={() => document.getElementById('image-upload-input')?.click()}
              >
                파일 직접 선택
              </Button>
            </div>

            {/* 이미지 보드 */}
            <div
              style={{
                height: imageLabellingFormHeight
                  ? `${imageLabellingFormHeight - styleConstant.fileSelectButtonLayoutImageUploadPage}px`
                  : 'auto',
                overflow: 'hidden'
              }}
            >
              <div
                onDrop={
                  isUploadDisabled
                    ? undefined
                    : (e) => {
                        // 기본 동작 방지 (새 탭 열림 방지)
                        e.preventDefault();
                        e.stopPropagation();

                        if (isFetchAIRecommendationLoading) {
                          showToast(
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Text variant="body1" color={colorSet.white} fontWeight="bold">
                                {ToastMessageEnum.CANNOT_ATTACH_IMAGE_DURING_AI_FETCHING}
                              </Text>
                              <Text variant="body1" color={colorSet.white}>
                                {ToastMessageEnum.RETRY_AFTER_AI_FETCHING}
                              </Text>
                            </div>,
                            ToastTypeEnum.ERROR
                          );
                        } else {
                          handleAttachDrop(e);
                        }
                      }
                }
                onDragOver={isUploadDisabled ? undefined : handleAttachDragOver}
                onDragEnter={isUploadDisabled ? undefined : handleAttachDragEnter}
                style={{
                  backgroundColor: isUploadDisabled ? colorSet.white : colorSet.gray100,
                  height: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {imageList && imageList.length > 0 ? (
                  <div style={{ height: '100%', padding: hasScrollbar ? '16px 16px 16px 40px' : '16px 40px' }}>
                    <ScrollList ref={scrollListRef} className={hasScrollbar ? 'has-scrollbar' : ''}>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: `15px ${horizontalGap}px`,
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        {imageList.map((item, index) => {
                          return (
                            <div
                              key={index}
                              draggable
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDragDrop(e, index)}
                              onClick={() => handleImageFocus(item, index)}
                              style={{
                                position: 'relative',
                                flex: `0 0 ${itemSize}`,
                                width: itemSize,
                                aspectRatio: '200 / 224',
                                overflow: 'hidden',
                                padding: '10px',
                                cursor: 'grab', // 드래그 가능함을 표시하는 커서
                                transition: 'transform 0.2s', // 부드러운 움직임
                                backgroundColor: item.isFocus ? 'rgba(15, 15, 15, 0.06)' : 'transparent'
                              }}
                            >
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '12px',
                                  backgroundColor:
                                    isSaveAttempted &&
                                    imageList[index].childOptions &&
                                    (imageList[index].childOptions?.femaleBody == null ||
                                      imageList[index].childOptions?.sexualBodyPart == null ||
                                      imageList[index].childOptions?.characterNumber == null ||
                                      imageList[index].childOptions?.shot == null ||
                                      imageList[index].childOptions?.angleFirst == null ||
                                      imageList[index].childOptions?.angleSecond == null)
                                      ? colorSet.errorMain
                                      : colorSet.gray600
                                }}
                              >
                                <Text variant="body2" color="white" fontWeight="bold">
                                  {item.order ?? 1}
                                </Text>
                              </div>
                              <div style={{ width: '100%', height: 'calc(100% - 24px)' }}>
                                {item.imageId ? (
                                  <SecureImage
                                    imageUrl={item.url ?? ''}
                                    imageStyle={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      pointerEvents: 'none' // 이미지 자체는 드래그되지 않도록
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={item.url}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      pointerEvents: 'none' // 이미지 자체는 드래그되지 않도록
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollList>
                  </div>
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {isUploadDisabled ? null : (
                      <div
                        style={{
                          width: 180,
                          height: 216,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <img src={drag_and_drop_icon} style={{ width: 180, height: 180 }} />
                        <Text variant="h2" color={colorSet.gray300}>
                          Drag & Drop
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 우측 라벨링 폼 */}
          <div
            ref={imageLabellingFormRef}
            style={{
              width: 520,
              padding: '24px 40px 40px 40px',
              overflow: 'hidden',
              borderLeft: `1px solid ${colorSet.gray300}`,
              boxSizing: 'border-box',
              minHeight: `calc(100vh - ${styleConstant.menuTopHeight + styleConstant.topFilterImageUploadPage}px)`
            }}
          >
            {focusedImage ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <Text variant="subtitle" fontWeight="bold">
                    {/* 2장 이상의 이미지 존재 시 order 보여주기 */}
                    {focusedImage && imageList && imageList.length > 1 ? focusedImage.order : ''}
                  </Text>
                  {/* 이미지 수정 페이지 - 기존에 업로드된 이미지는 삭제 불가 */}
                  {focusedImage.imageId ? null : (
                    <Button
                      onClick={handleImageDelete}
                      variant="outlined"
                      color="primary"
                      style={{ width: 84, height: 44 }}
                    >
                      <img src={trash_can_primary} style={{ width: 20, height: 20 }} />
                    </Button>
                  )}
                </div>
                <div>
                  <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
                    파일명
                  </Text>
                  <Text variant="body1">{focusedImage.name}</Text>
                </div>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      marginBottom: '8px'
                    }}
                  >
                    <Text variant="subtitle" fontWeight="bold">
                      필터
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                      <StarsSecondaryIcon />
                      <Text variant="body1" color={colorSet.gray500}>
                        AI 추천 필터
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label="여체"
                        options={{
                          슬랜더: {
                            name: '슬랜더',
                            value: FemaleBodyChildOptionValueEnum['슬랜더'],
                            isSelected: false
                          },
                          글래머: {
                            name: '글래머',
                            value: FemaleBodyChildOptionValueEnum['글래머'],
                            isSelected: false
                          },
                          슈퍼글래머: {
                            name: '슈퍼글래머',
                            value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                            isSelected: false
                          },
                          통통: { name: '통통', value: FemaleBodyChildOptionValueEnum['통통'], isSelected: false }
                        }}
                        storedChildOptionValue={(focusedImage?.childOptions?.['femaleBody'] as IOptionList) ?? null}
                        setSelectedOption={(param) => updateFocusedImageChildOptions('femaleBody', param)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.femaleBody == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={!(focusedImage?.childOptions?.['femaleBody'] as IOptionList)}
                        isLoading={
                          isFetchAIRecommendationLoading && !(focusedImage?.childOptions?.['femaleBody'] as IOptionList)
                        }
                        recommendedChildOptionValue={
                          (focusedImage?.aiRecommendedChildOptions?.['femaleBody'] as IOptionList) ?? null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'femaleBody',
                            focusedImage?.aiRecommendedChildOptions?.['femaleBody'] as IOptionList
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label="신체 부위"
                        paperType={ChipPaperType.CHECKBOX_LIST}
                        options={{
                          가슴: { name: '가슴', value: SexualBodyPartChildOptionValueEnum['가슴'], isSelected: false },
                          엉덩이: {
                            name: '엉덩이',
                            value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                            isSelected: false
                          },
                          '여자 성기': {
                            name: '여자 성기',
                            value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                            isSelected: false
                          },
                          '남자 성기': {
                            name: '남자 성기',
                            value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                            isSelected: false
                          },
                          입: {
                            name: '입',
                            value: SexualBodyPartChildOptionValueEnum['입'],
                            isSelected: false
                          },
                          얼굴: {
                            name: '얼굴',
                            value: SexualBodyPartChildOptionValueEnum['얼굴'],
                            isSelected: false
                          }
                        }}
                        storedChildOptionValue={(focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList) ?? null}
                        setSelectedOption={(param) => updateFocusedImageChildOptions('sexualBodyPart', param)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.sexualBodyPart == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={!(focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList)}
                        isLoading={
                          isFetchAIRecommendationLoading &&
                          !(focusedImage?.childOptions?.['sexualBodyPart'] as IOptionList)
                        }
                        recommendedChildOptionValue={
                          (focusedImage?.aiRecommendedChildOptions?.['sexualBodyPart'] as IOptionList) ?? null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'sexualBodyPart',
                            focusedImage?.aiRecommendedChildOptions?.['sexualBodyPart'] as IOptionList
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <SimpleCharacterNumberInput
                        characterNumber={
                          typeof focusedImage?.childOptions?.characterNumber === 'object' &&
                          focusedImage?.childOptions?.characterNumber !== null
                            ? (focusedImage.childOptions.characterNumber as {
                                maleNumber: number;
                                femaleNumber: number;
                              })
                            : null
                        }
                        onApplyClick={(values) => updateFocusedImageChildOptions('characterNumber', values)}
                        onChipXButtonClick={() => updateFocusedImageChildOptions('characterNumber', null)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.characterNumber == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={
                          !(focusedImage.childOptions?.characterNumber as {
                            maleNumber: number;
                            femaleNumber: number;
                          })
                        }
                        isLoading={
                          isFetchAIRecommendationLoading &&
                          !(focusedImage.childOptions?.characterNumber as {
                            maleNumber: number;
                            femaleNumber: number;
                          })
                        }
                        recommendedCharacterNumber={
                          typeof focusedImage?.aiRecommendedChildOptions?.characterNumber === 'object' &&
                          focusedImage?.aiRecommendedChildOptions?.characterNumber !== null &&
                          !Array.isArray(focusedImage?.aiRecommendedChildOptions?.characterNumber)
                            ? `남자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.maleNumber}명 ∙ 여자 ${focusedImage?.aiRecommendedChildOptions?.characterNumber.femaleNumber}명`
                            : null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'characterNumber',
                            focusedImage?.aiRecommendedChildOptions?.characterNumber
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label="프레임"
                        options={{
                          풀샷: {
                            name: '풀샷',
                            value: FrameChildOptionValueEnum['풀샷'],
                            isSelected: false
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: FrameChildOptionValueEnum['미디엄샷'],
                            isSelected: false
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: FrameChildOptionValueEnum['클로즈업'],
                            isSelected: false
                          }
                        }}
                        storedChildOptionValue={(focusedImage?.childOptions?.shot as IOptionList) ?? null}
                        setSelectedOption={(param) => updateFocusedImageChildOptions('shot', param)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.shot == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={!(focusedImage?.childOptions?.shot as IOptionList)}
                        isLoading={isFetchAIRecommendationLoading && !(focusedImage?.childOptions?.shot as IOptionList)}
                        recommendedChildOptionValue={
                          (focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList) ?? null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'shot',
                            focusedImage?.aiRecommendedChildOptions?.['shot'] as IOptionList
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label="가로앵글"
                        paperType={ChipPaperType.CHECKBOX_LIST}
                        options={{
                          정면: { name: '정면', value: HorizontalAngleChildOptionValueEnum['정면'], isSelected: false },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: false
                          },
                          측면: { name: '측면', value: HorizontalAngleChildOptionValueEnum['측면'], isSelected: false },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: false
                          },
                          후면: { name: '후면', value: HorizontalAngleChildOptionValueEnum['후면'], isSelected: false }
                        }}
                        storedChildOptionValue={(focusedImage?.childOptions?.['angleFirst'] as IOptionList) ?? null}
                        setSelectedOption={(param) => updateFocusedImageChildOptions('angleFirst', param)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.angleFirst == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={!(focusedImage?.childOptions?.['angleFirst'] as IOptionList)}
                        isLoading={
                          isFetchAIRecommendationLoading && !(focusedImage?.childOptions?.['angleFirst'] as IOptionList)
                        }
                        recommendedChildOptionValue={
                          (focusedImage?.aiRecommendedChildOptions?.['angleFirst'] as IOptionList) ?? null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'angleFirst',
                            focusedImage?.aiRecommendedChildOptions?.['angleFirst'] as IOptionList
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Chip
                        label="세로앵글"
                        options={{
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: false
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: false
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: false
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: false
                          }
                        }}
                        storedChildOptionValue={(focusedImage?.childOptions?.['angleSecond'] as IOptionList) ?? null}
                        setSelectedOption={(param) => updateFocusedImageChildOptions('angleSecond', param)}
                        isError={isSaveAttempted && focusedImage?.childOptions?.angleSecond == null}
                      />
                      <AIRecommendedChildOptionValue
                        isVisible={!(focusedImage?.childOptions?.['angleSecond'] as IOptionList)}
                        isLoading={
                          isFetchAIRecommendationLoading &&
                          !(focusedImage?.childOptions?.['angleSecond'] as IOptionList)
                        }
                        recommendedChildOptionValue={
                          (focusedImage?.aiRecommendedChildOptions?.['angleSecond'] as IOptionList) ?? null
                        }
                        onClick={() =>
                          updateFocusedImageChildOptions(
                            'angleSecond',
                            focusedImage?.aiRecommendedChildOptions?.['angleSecond'] as IOptionList
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: '8px' }}
                  >
                    <Text variant="subtitle" fontWeight="bold">
                      관련 태그
                    </Text>
                    <Text variant="body1" color={colorSet.gray400}>
                      {'(Optional)'}
                    </Text>
                  </div>
                  <SearchInput
                    placeholder="태그 입력 후 스페이스바를 누르세요."
                    boxstyle={{ marginBottom: '10px' }}
                    value={tagInputKeyword}
                    options={tagAutocompleteList}
                    onKeyDown={handleTagKeyDown}
                    onInputChange={handleTagInputKeywordChange}
                    onAutocompleteChange={handleTagAutocompleteChange}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: `10px`,
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {focusedImage.tags && focusedImage.tags.length > 0
                      ? focusedImage.tags.map((item, index) => {
                          return (
                            <div
                              key={index}
                              style={{
                                height: 28,
                                border: `1px solid ${colorSet.gray300}`,
                                borderRadius: '6px',
                                padding: '0px 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <Text variant="body1" marginRight="2px">
                                {item}
                              </Text>
                              <img
                                src={x_gray400}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                onClick={() => {
                                  deleteFocusedImageTag(item);
                                }}
                              />
                            </div>
                          );
                        })
                      : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 24, marginBottom: 10 }}>
                    <StarsSecondaryIcon />
                    <Text variant="body1" color={colorSet.gray500}>
                      AI 추천 태그
                    </Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: `10px`,
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {isFetchAIRecommendationLoading ? <LoadingSpinner /> : null}
                    {focusedImage.aiRecommendedTags && focusedImage.aiRecommendedTags.length > 0
                      ? focusedImage.aiRecommendedTags
                          .filter((aiTag) => !focusedImage.tags?.includes(aiTag)) //이미 tags에 추가된 값은 제외하기
                          .map((item, index) => {
                            return (
                              <AIRecommendedTag
                                key={index}
                                recommendedTag={item}
                                onClick={() => {
                                  addFocusedImageTag(item);
                                }}
                              />
                            );
                          })
                      : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <GuideModal
        isVisible={isModalVisible}
        onClick={handleModalConfirmClick}
        onClose={() => {
          if (
            modalType === ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS ||
            modalType === ImageUploaderModalType.OMIT_REQUIRED_INPUT
          ) {
            handleModalConfirmClick();
          } else {
            setIsModalVisible(false);
          }
        }}
        imgSrc={modalType === ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS ? check_circle_primary : undefined}
        title={modalTitle}
        content={modalContent}
        buttonText={createModalButtonText()}
        buttonVariant={createModalButtonVariant()}
        buttonCount={createModalButtonCount()}
      />
    </>
  );
};

export default ImageUploaderImageUploadPage;
